import { useEffect, useRef, useState } from "react";
import { AlertTriangle, ArrowRight, CheckCircle2, CircleSlash, Download, Eye, FileText, Link2, ListTree, Loader2, MousePointerClick, ScanSearch, ShieldCheck, Sparkles, X } from "lucide-react";
import SiteFooter from "@/components/SiteFooter";
import PublicHeader from "@/components/PublicHeader";
import { useHeadMeta } from "@/lib/head";
import { CHECK_ENDPOINT, GROUPS, SCOPE_POINTS, formatCheckedAt, groupFindings, interpretResponse, reportFilename, reportHtml, reportMarkdown, safeHref, summarise } from "@/lib/seoCheck.mjs";
import { BENEFITS, EXAMPLE_COUNTS, EXAMPLE_FINDINGS, EXAMPLE_REDIRECTS, FAQS, REPORT_ROWS } from "@/content/seoCheck";
import "./HomepageFonts.css";
import "./Homepage.css";
import "./SeoCheck.css";

const ICONS = { issue: AlertTriangle, review: Eye, unavailable: CircleSlash, passed: CheckCircle2 };
const BENEFIT_ICONS = [MousePointerClick, ScanSearch, Sparkles];
const ROW_ICONS = [FileText, ListTree, ShieldCheck, Link2, Download];
const CLIENT_TIMEOUT_MS = 25_000;
const groupFor = status => GROUPS.find(g => g.status === status);

function UrlText({ value }) {
  const href = safeHref(value);
  return href ? <a href={href} rel="noopener noreferrer nofollow" target="_blank">{value}</a> : <span>{value}</span>;
}

// Shared by live results and the example cards. `Title` is a heading in live
// results and plain text in examples, so examples never add to the outline.
function Finding({ finding, as: Title = "h4", compact = false }) {
  const group = groupFor(finding.status);
  const Icon = ICONS[group.status];
  return <li className={`ss-finding ss-finding-${group.status}${compact ? " ss-finding-compact" : ""}`}>
    <div className="ss-finding-head">
      <span className={`ss-pill ss-pill-${group.status}`}><Icon size={14} aria-hidden="true" />{group.single}</span>
      <span className="ss-finding-check">{finding.check}</span>
    </div>
    <Title className="ss-finding-title">{finding.title}</Title>
    {finding.evidence?.length > 0 && <dl className="ss-evidence-list" aria-label={`Evidence for ${finding.check}`}>
      {finding.evidence.map((item, index) => <div key={index}><dt>{item.label}</dt><dd>{item.value}</dd></div>)}
    </dl>}
    <p>{finding.explanation}</p>
    {!compact && <p className="ss-next"><strong>Next step:</strong> {finding.nextStep}</p>}
  </li>;
}

function Counts({ counts }) {
  return <ul className="ss-counts">
    {GROUPS.map(g => { const Icon = ICONS[g.status]; return <li key={g.status} className={`ss-count-${g.status}`}><Icon size={16} aria-hidden="true" /><strong>{counts[g.status] || 0}</strong><span>{g.label}</span></li>; })}
  </ul>;
}

function Redirects({ redirects }) {
  return <ol className="ss-redirect-list">{redirects.map((hop, i) => <li key={i}>
    <span className={`ss-code ss-code-${[301, 308].includes(hop.status) ? "perm" : "temp"}`}>{hop.status}</span>
    <span className="ss-url">{hop.url}</span>
    <span className="ss-hop-arrow" role="img" aria-label="redirects to">↓</span>
    <span className="ss-url">{hop.nextUrl || hop.location}</span>
  </li>)}</ol>;
}

function ExampleCard({ label, children, className = "" }) {
  return <figure className={`ss-example ${className}`}>
    <figcaption><span>Example</span>{label}</figcaption>
    {children}
  </figure>;
}

function Exports({ result }) {
  const [message, setMessage] = useState("");
  const markdown = () => {
    try {
      const url = URL.createObjectURL(new Blob([reportMarkdown(result)], { type: "text/markdown;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url; link.download = reportFilename(result, "md");
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setMessage("Markdown download started.");
    } catch { setMessage("The report could not be exported. Please try again."); }
  };
  const pdf = () => {
    // Opened synchronously from the click so popup rules are respected.
    const popup = window.open("", "_blank");
    if (!popup) { setMessage("Allow a new tab for this site, then try Export PDF again."); return; }
    try {
      popup.opener = null;
      popup.document.write(reportHtml(result));
      popup.document.close();
      const print = () => { popup.focus(); popup.print(); };
      popup.document.getElementById("save-pdf").addEventListener("click", print);
      popup.requestAnimationFrame(() => popup.requestAnimationFrame(print));
      setMessage("PDF view opened. Choose Save as PDF in the print dialog.");
    } catch { popup.close(); setMessage("The PDF view could not be opened. Please try again."); }
  };
  return <div className="ss-exports">
    <div role="group" aria-label="Export this report" className="ss-export-buttons">
      <button type="button" className="ss-button ss-secondary" onClick={markdown} data-testid="seo-check-export-md"><Download size={16} aria-hidden="true" />Download Markdown</button>
      <button type="button" className="ss-button ss-secondary" onClick={pdf} data-testid="seo-check-export-pdf"><FileText size={16} aria-hidden="true" />Export PDF</button>
    </div>
    <p role="status" aria-live="polite" className="ss-export-message">{message}</p>
    <p className="ss-small">Reports are generated in your browser. Nothing is saved or published.</p>
  </div>;
}

function Results({ result, headingRef }) {
  const all = groupFindings(result.findings);
  const groups = all.filter(g => g.findings.length);
  const counts = Object.fromEntries(all.map(g => [g.status, g.findings.length]));
  const unavailable = result.outcome === "unavailable";
  return <section className="ss-results-band" aria-labelledby="seo-check-results-heading" data-testid="seo-check-results">
    <div className="ss-container ss-results">
      <p className="ss-eyebrow">YOUR REPORT</p>
      <h2 id="seo-check-results-heading" ref={headingRef} tabIndex={-1}>{unavailable ? "Couldn’t check this URL" : "Results"}</h2>
      {unavailable && <p className="ss-lead ss-unavailable-reason">{result.reason?.message}</p>}
      <div className="ss-results-top">
        <dl className="ss-result-meta">
          <div><dt>Submitted URL</dt><dd><UrlText value={result.submittedUrl} /></dd></div>
          {result.finalUrl && <div><dt>Final URL</dt><dd><UrlText value={result.finalUrl} /></dd></div>}
          {result.response && <div><dt>HTTP status</dt><dd>{`${result.response.status} ${result.response.statusText || ""}`.trim()}</dd></div>}
          <div><dt>Checked</dt><dd><time dateTime={result.checkedAt}>{formatCheckedAt(result.checkedAt)}</time></dd></div>
        </dl>
        {!unavailable && <div className="ss-results-counts"><p className="ss-summary" data-testid="seo-check-summary">{summarise(result.findings)}</p><Counts counts={counts} /></div>}
      </div>
      {result.redirects?.length > 0 && <div className="ss-redirects"><h3>Redirects followed</h3><Redirects redirects={result.redirects} /></div>}
      {groups.map(group => <section key={group.status} className="ss-group" aria-labelledby={`seo-check-group-${group.status}`}>
        <h3 id={`seo-check-group-${group.status}`}>{group.label} <span className="ss-count">({group.findings.length})</span></h3>
        <p className="ss-small">{group.blurb}</p>
        <ul className="ss-findings">{group.findings.map(f => <Finding key={f.id} finding={f} />)}</ul>
      </section>)}
      <p className="ss-scope-reminder"><ShieldCheck size={16} aria-hidden="true" /> {result.scope}</p>
      <Exports result={result} />
    </div>
  </section>;
}

function HeroCollage() {
  return <div className="ss-collage" aria-hidden="true">
    <ExampleCard label="Summary" className="ss-collage-counts"><Counts counts={EXAMPLE_COUNTS} /></ExampleCard>
    <ExampleCard label="Finding" className="ss-collage-finding"><ul className="ss-findings"><Finding finding={EXAMPLE_FINDINGS.canonical} as="p" compact /></ul></ExampleCard>
    <ExampleCard label="Redirects" className="ss-collage-redirects"><Redirects redirects={EXAMPLE_REDIRECTS} /></ExampleCard>
  </div>;
}

const MD_SAMPLE = `# SEO Signals single-page check

Checked: 5 Oct 2026, 17:00:00 UTC

## Review (3)

### Heading order: 1 skipped heading level
- Gap 1
  H2 → H4: Opening hours

Next step: Use heading levels for
structure and CSS for size.`;

function ReportExample({ kind }) {
  if (kind === "titles") return <ExampleCard label="Title and description"><ul className="ss-findings"><Finding finding={EXAMPLE_FINDINGS.title} as="p" compact /><Finding finding={EXAMPLE_FINDINGS.description} as="p" compact /></ul></ExampleCard>;
  if (kind === "headings") return <ExampleCard label="Heading order"><ul className="ss-findings"><Finding finding={EXAMPLE_FINDINGS.headings} as="p" /></ul></ExampleCard>;
  if (kind === "indexing") return <ExampleCard label="Canonical and robots"><ul className="ss-findings"><Finding finding={EXAMPLE_FINDINGS.canonical} as="p" compact /><Finding finding={EXAMPLE_FINDINGS.noindex} as="p" compact /></ul></ExampleCard>;
  if (kind === "redirects") return <ExampleCard label="Status and redirects"><Redirects redirects={EXAMPLE_REDIRECTS} /><ul className="ss-findings"><Finding finding={EXAMPLE_FINDINGS.status} as="p" compact /></ul></ExampleCard>;
  return <ExampleCard label="Markdown export" className="ss-example-export">
    <pre className="ss-md-preview">{MD_SAMPLE}</pre>
    <div className="ss-export-preview"><span><Download size={14} aria-hidden="true" />Download Markdown</span><span><FileText size={14} aria-hidden="true" />Export PDF</span></div>
  </ExampleCard>;
}

export default function SeoCheckPage() {
  useHeadMeta({ title: "Free single-page SEO check | SEO Signals", robots: "index, follow" });
  const [url, setUrl] = useState("");
  const [state, setState] = useState({ phase: "idle" });
  const headingRef = useRef(null);
  const inputRef = useRef(null);
  const busy = state.phase === "loading";

  useEffect(() => {
    if (state.phase === "done") headingRef.current?.focus();
    if (state.phase === "error") inputRef.current?.focus();
  }, [state.phase]);

  const submit = async event => {
    event.preventDefault();
    if (busy) return;
    if (!url.trim()) { setState({ phase: "error", error: "Enter a URL to check." }); return; }
    setState({ phase: "loading" });
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), CLIENT_TIMEOUT_MS);
    try {
      const response = await fetch(CHECK_ENDPOINT, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ url: url.trim() }),
        credentials: "omit", cache: "no-store", referrerPolicy: "no-referrer", signal: controller.signal,
      });
      let body = null;
      try { body = await response.json(); } catch {}
      const { result, error } = interpretResponse(response.status, body);
      setState(result ? { phase: "done", result } : { phase: "error", error });
    } catch {
      setState({ phase: "error", error: "The check could not be completed. Check your connection and try again." });
    } finally { clearTimeout(timer); }
  };

  const status = busy ? "Checking the page. This can take up to 10 seconds."
    : state.phase === "done" ? (state.result.outcome === "unavailable" ? "Check finished. The URL couldn’t be checked." : `Check finished: ${summarise(state.result.findings)}.`)
    : "";

  return <div className="ss-home ss-tool">
    <a className="ss-skip" href="#check">Skip to the checker</a>
    <PublicHeader />
    <main id="main">
      <section className="ss-tool-hero" aria-labelledby="seo-check-title">
        <div className="ss-container ss-tool-hero-grid">
          <div className="ss-tool-hero-copy" id="check">
            <p className="ss-eyebrow">FREE SINGLE-PAGE SEO CHECK</p>
            <h1 id="seo-check-title">Check one page’s <span className="ss-highlight">SEO basics</span></h1>
            <p className="ss-lead">Enter a URL. SEO Signals fetches the HTML that URL returns and shows what it found, with the evidence for each result.</p>
            <form className="ss-check-form" onSubmit={submit} noValidate data-testid="seo-check-form">
              <label htmlFor="seo-check-url">Page URL</label>
              <div className="ss-check-row">
                <input id="seo-check-url" ref={inputRef} name="url" type="text" inputMode="url" autoComplete="url" autoCapitalize="none" spellCheck={false}
                  maxLength={2048} placeholder="https://example.com/page" value={url} onChange={e => setUrl(e.target.value)}
                  aria-describedby={state.phase === "error" ? "seo-check-hint seo-check-error" : "seo-check-hint"} aria-invalid={state.phase === "error" ? "true" : undefined} data-testid="seo-check-input" />
                <button type="submit" className="ss-button ss-primary" disabled={busy} data-testid="seo-check-submit">
                  {busy ? <Loader2 size={18} className="ss-spin" aria-hidden="true" /> : null}{busy ? "Checking…" : "Check page"}{busy ? null : <ArrowRight size={18} aria-hidden="true" />}
                </button>
              </div>
              <p id="seo-check-hint" className="ss-hint">Free. No sign-in. Public http or https pages. We don’t store the page or log the URL.</p>
              {state.phase === "error" && <p id="seo-check-error" className="ss-form-error" role="alert" data-testid="seo-check-error"><AlertTriangle size={16} aria-hidden="true" /> {state.error}</p>}
            </form>
            <p className="ss-sr" role="status" aria-live="polite" data-testid="seo-check-status">{status}</p>
            {busy && <p className="ss-loading" aria-hidden="true"><Loader2 size={16} className="ss-spin" /> Fetching the page and reading its HTML…</p>}
            <ul className="ss-hero-ticks"><li><CheckCircle2 size={16} aria-hidden="true" />Evidence for every result</li><li><CheckCircle2 size={16} aria-hidden="true" />Markdown and PDF export</li><li><CheckCircle2 size={16} aria-hidden="true" />No account needed</li></ul>
          </div>
          <HeroCollage />
        </div>
      </section>

      {state.phase === "done" && <Results result={state.result} headingRef={headingRef} />}

      <section className="ss-container ss-section ss-benefits" aria-labelledby="seo-check-why">
        <h2 id="seo-check-why">Why use this check?</h2>
        <div className="ss-benefit-grid">{BENEFITS.map((b, i) => { const Icon = BENEFIT_ICONS[i]; return <div key={b.title} className="ss-benefit"><span className="ss-benefit-icon"><Icon size={26} aria-hidden="true" /></span><div><h3>{b.title}</h3><p>{b.text}</p></div></div>; })}</div>
      </section>

      <section className="ss-scope-band" aria-labelledby="seo-check-scope-heading" data-testid="seo-check-scope">
        <div className="ss-container">
          <h2 id="seo-check-scope-heading">What this check covers</h2>
          <p className="ss-scope-lead"><strong>One returned HTML response and up to three redirects.</strong> It is not a site audit, and it cannot tell you whether Google has indexed the page.</p>
          <div className="ss-scope-grid">
            <div className="ss-scope-col ss-scope-yes"><h3><CheckCircle2 size={20} aria-hidden="true" />Checks</h3><ul>{SCOPE_POINTS.checks.map(item => <li key={item}><CheckCircle2 size={16} aria-hidden="true" />{item}</li>)}</ul></div>
            <div className="ss-scope-col ss-scope-no"><h3><X size={20} aria-hidden="true" />Does not check</h3><ul>{SCOPE_POINTS.excludes.map(item => <li key={item}><X size={16} aria-hidden="true" />{item}</li>)}</ul></div>
          </div>
        </div>
      </section>

      <section className="ss-section ss-report-tour" aria-labelledby="seo-check-tour">
        <div className="ss-container">
          <p className="ss-eyebrow">WHAT’S IN THE REPORT</p>
          <h2 id="seo-check-tour">Every result shows what we found and what to do next</h2>
          <p className="ss-tour-intro">Results are grouped as Issues, Review, Couldn’t check and Passed. Here is what each part of the report looks like.</p>
          {REPORT_ROWS.map((row, i) => { const Icon = ROW_ICONS[i]; return <div key={row.key} className={`ss-tour-row${i % 2 ? " ss-tour-flip" : ""}`}>
            <div className="ss-tour-visual"><ReportExample kind={row.key} /></div>
            <div className="ss-tour-copy">
              <span className="ss-tour-icon"><Icon size={22} aria-hidden="true" /></span>
              <h3>{row.title}</h3>
              <p>{row.text}</p>
              <a className="ss-button ss-primary" href="#check">Check a page <ArrowRight size={16} aria-hidden="true" /></a>
            </div>
          </div>; })}
        </div>
      </section>

      <section className="ss-section ss-surface" aria-labelledby="seo-check-faq">
        <div className="ss-container ss-faq">
          <p className="ss-eyebrow">QUESTIONS</p>
          <h2 id="seo-check-faq">Straight answers</h2>
          <div>{FAQS.map(item => <details key={item.q}><summary>{item.q}</summary><p>{item.a}</p></details>)}</div>
        </div>
      </section>

      <section className="ss-gsc-band" aria-labelledby="seo-check-gsc-heading" data-testid="seo-check-gsc-invite">
        <div className="ss-container ss-gsc-grid">
          <div>
            <p className="ss-eyebrow">OPTIONAL</p>
            <h2 id="seo-check-gsc-heading">See how Google treats your pages</h2>
            <p>This check reads one response. Search Console shows what Google reports: clicks, impressions and the pages that need attention. Connect it to SEO Signals for up to three evidence-based fixes. Your free check results stay the same either way.</p>
          </div>
          <div className="ss-gsc-actions">
            <a className="ss-button ss-primary" href="/#how">How connecting Search Console works <ArrowRight size={16} aria-hidden="true" /></a>
            <a className="ss-button ss-ghost" href="#check">Check another page</a>
          </div>
        </div>
      </section>
    </main>
    <SiteFooter />
  </div>;
}
